import { HIDDEN_CLASS } from "@smartrr/shared/constants";
import { IGift } from "@smartrr/shared/entities/Gift";
import { PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import { useCallback, useEffect } from "react";

import { useMobileQuery } from "@customer-app/account/views/OperationAquarius/utils";
import { useUpdateModalProps } from "@customer-app/state/actionCreators/modal";

import { ACTIVE_CLASS, CENTERED_CLASS, SELECTED_CLASS } from "../../../constants";
import { ButtonOption, ManageSubscriptionRefs, ViewInfo } from "../../types";

export const useModalButtons = (
  delivery: PurchaseStateAndDelivery,
  viewInfo: ViewInfo,
  modalRefs: ManageSubscriptionRefs,
  buttonOptions: ButtonOption[],
  gift?: IGift
) => {
  const isMobile = useMobileQuery();
  const updateModalProps = useUpdateModalProps();

  useEffect(() => {
    if (viewInfo.initial !== "Default" && viewInfo.view !== "Default") {
      for (const [index, opt] of buttonOptions.entries()) {
        if (opt && opt.view === viewInfo.initial) {
          setIconButtonActive(index);
        }
      }
    }
  }, [viewInfo]);

  useEffect(() => {
    modalRefs.options.current = modalRefs.options.current.slice(0, buttonOptions.length);
  }, [buttonOptions]);

  const setIconButtonActive = useCallback(
    index => {
      for (const [i, e] of buttonOptions.entries()) {
        const ele = modalRefs.options.current[i];
        if (ele) {
          const left = ele.offsetLeft ?? 0;
          /**
           * The two setTimeouts are needed as the offsetLeft lookup happens AFTER the if statement for some reason
           * resulting in all of the buttons not staying in their original positions before moving to center
           */
          setTimeout(() => {
            if (index === i) {
              viewInfo.set(e?.view!);
              if (isMobile) {
                modalRefs.button.set(input => input.classList.toggle("just-center"));
                ele.classList.add(CENTERED_CLASS);
              } else {
                ele.classList.add(SELECTED_CLASS);
                ele.style.left = `${left}px`; // this is needed so the animation is smooth (old position -> new left position)
                modalRefs.box.set(div => {
                  div.style.height =
                    ele.getBoundingClientRect().height < 90
                      ? "90px"
                      : ele.getBoundingClientRect().height + 10 + "px";
                });
                setTimeout(() => {
                  ele.classList.add(ACTIVE_CLASS);
                }, 500);
              }
            } else {
              ele.classList.add(HIDDEN_CLASS); // hiding all other buttons
            }
          }, 0);
        }
      }
    },
    [modalRefs, buttonOptions]
  );

  const setIconButtonInactive = useCallback(() => {
    if (!delivery.isSkipped || !gift) {
      for (const [i, e] of buttonOptions.entries()) {
        const ele = modalRefs.options.current[i];
        if (ele.classList.contains(ACTIVE_CLASS) || ele.classList.contains(CENTERED_CLASS)) {
          if (isMobile) {
            modalRefs.button.set(input => input.classList.remove("just-center"));
            ele.classList.remove(CENTERED_CLASS);
          } else {
            ele.classList.remove(ACTIVE_CLASS);
            setTimeout(() => {
              ele.classList.remove(SELECTED_CLASS);
            }, 1000);
          }
        }
      }

      setTimeout(
        () => {
          for (const [i, e] of buttonOptions.entries()) {
            const ele = modalRefs.options.current[i];
            ele.classList.remove(HIDDEN_CLASS);
          }
        },
        isMobile ? 0 : 1000
      );

      setTimeout(
        () => {
          viewInfo.set("Default");
          updateModalProps({ propOverrides: { initialView: "Default" } });
          modalRefs.box.set(div => {
            div.style.height = "max-content";
          });
        },
        isMobile ? 0 : 1000
      );
    } else {
      setTimeout(() => {
        viewInfo.set("Default");
        updateModalProps({ propOverrides: { initialView: "Default" } });
      }, 500);
    }
  }, [buttonOptions]);

  return {
    setIconButtonActive,
    setIconButtonInactive,
  };
};
